import { useState, useEffect } from "react";
import { supabase } from "@/index";
import { useAuth } from "@/providers/AuthProvider";
import { Button } from "@/components/ui/button";
import { PageHeading } from "./ui/page-heading";
import { PageContent } from "./ui/page-content";
import { ErrorDisplay } from "./ui/error";
import { Input } from "./ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

export function Profile() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [showQRCode, setShowQRCode] = useState(false);
  const [factorId, setFactorId] = useState<string>();
  const [qrCode, setQrCode] = useState<string>();
  const [verifyCode, setVerifyCode] = useState("");
  const [has2FA, setHas2FA] = useState(false);

  useEffect(() => {
    checkMFAStatus();
  }, []);

  async function checkMFAStatus() {
    try {
      const { data: factors } = await supabase.auth.mfa.listFactors();
      setHas2FA((factors?.totp?.length || 0) > 0);
    } catch (err: any) {
      setError(err.message);
    }
  }

  async function handleEnable2FA() {
    try {
      setIsLoading(true);
      setError("");

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: "totp",
      });

      if (error) throw error;

      if (data) {
        setFactorId(data.id);
        setQrCode(data.totp.qr_code);
        setShowQRCode(true);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleVerify2FA() {
    try {
      setIsLoading(true);
      setError("");

      if (!factorId) throw new Error("No factor ID found");

      const { error, data: challengeData } = await supabase.auth.mfa.challenge({
        factorId: factorId,
      });

      if (error) throw error;

      const { data, error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factorId,
        code: verifyCode,
        challengeId: challengeData?.id,
      });

      if (verifyError) throw verifyError;

      if (data) {
        setShowQRCode(false);
        setVerifyCode("");
        checkMFAStatus();
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleDisable2FA() {
    try {
      setIsLoading(true);
      setError("");

      const { data: factors } = await supabase.auth.mfa.listFactors();
      const factor = factors?.totp[0];

      if (factor) {
        const { error } = await supabase.auth.mfa.unenroll({
          factorId: factor.id,
        });

        if (error) throw error;
        checkMFAStatus();
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <>
      <PageHeading>My profile</PageHeading>
      <PageContent>
        <Card>
          <CardHeader>
            <CardTitle>Account information</CardTitle>
            <CardDescription>
              Your account details and preferences
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <Label>Email</Label>
                <div className="text-sm text-muted-foreground">
                  {user?.email}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  checked={has2FA}
                  onCheckedChange={(checked) =>
                    checked ? handleEnable2FA() : handleDisable2FA()
                  }
                  disabled={isLoading}
                />
                <Label>Two-factor authentication</Label>
              </div>

              {error && <ErrorDisplay message={error} className="mt-2" />}
            </div>
          </CardContent>
        </Card>
      </PageContent>

      <Dialog open={showQRCode} onOpenChange={setShowQRCode}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set up two-factor authentication</DialogTitle>
            <DialogDescription>
              Scan this QR code with your authenticator app, then enter the code
              below to enable 2FA.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {qrCode && (
              <div className="flex justify-center">
                <img src={qrCode} alt="2FA QR Code" width={200} height={200} />
              </div>
            )}
            <div className="space-y-2">
              <Label>Verification code</Label>
              <Input
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value)}
                placeholder="Enter 6-digit code"
                maxLength={6}
              />
            </div>
            <Button
              onClick={handleVerify2FA}
              disabled={verifyCode.length !== 6 || isLoading}
              isLoading={isLoading}
              className="w-full"
            >
              Verify and enable
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
